/**
 * 冷启动恢复（任务 13b）：standalone 进程被系统回收 / 浏览器刷新后，回到
 * 「上次打开的会话 + 滚动位置」，像没关过一样。bfcache 热路径（页面没死）不经
 * 这里——wakeState 的 chat/wake 探活即可，DOM/store 都还在。
 *
 * 三块协作（恢复点本体存 Worker 账号库 kv，随 timeline open/close 自动记/清）：
 * - anchor 源注册表：MessageList 挂载时注册「取当前 anchor」回调，wakeState 在
 *   页面隐藏时经 flushRestoreAnchor 取走回传 Worker（chat/restore/anchor）。
 * - pending anchor：恢复导航前暂存，MessageList 首屏 rows 就绪后取走回放
 *   （consume-once；anchor 行不在首页时 virtualizer 自动回粘底）。
 * - connectColdStartRestore：只在「冷启动直接以登录态落在根路径」时恢复——
 *   URL 必须是 '/'（深链/Tab 直达尊重用户意图）、首个 auth 决议必须是 logged-in
 *   （经登录页手动登录不算「没关过」）、期间无任何导航（用户先动了就不打扰）。
 */

import { topRoute } from '~/nav/projection';
import type { GlobalStore } from './global';
import type { AppWorkerHost, AuthSlice } from './authState';
import type { VirtualAnchor } from '~/list/virtualizer';

interface AnchorSource {
  readonly key: string;
  readonly get: () => VirtualAnchor;
}

/** 打开中会话同时只有一个（messagesState 口径），单槽即可 */
let anchorSource: AnchorSource | null = null;

/** MessageList 挂载时注册（返回解除函数）。后注册者顶替（remount 竞态无害） */
export function registerAnchorSource(key: string, get: () => VirtualAnchor): () => void {
  const entry: AnchorSource = { key, get };
  anchorSource = entry;
  return () => {
    if (anchorSource === entry) anchorSource = null;
  };
}

/** 页面隐藏时把打开中会话的滚动 anchor 回传 Worker（wakeState onHide 调）。
 *  无打开中会话 / anchor 源未挂（首屏未就绪）时 no-op——恢复点仍有会话 key。 */
export function flushRestoreAnchor(store: GlobalStore, host: AppWorkerHost): void {
  const t = store.get().messages.timeline;
  if (!t || anchorSource === null || anchorSource.key !== t.key) return;
  void host
    .call('chat/restore/anchor', { key: t.key, anchor: anchorSource.get() })
    .catch(() => undefined);
}

let pendingAnchor: { key: string; anchor: VirtualAnchor } | null = null;

/** MessageList 首屏 rows 就绪后取走回放（consume-once；key 不符返回 null 不消耗） */
export function takeRestoredAnchor(key: string): VirtualAnchor | null {
  if (pendingAnchor === null || pendingAnchor.key !== key) return null;
  const { anchor } = pendingAnchor;
  pendingAnchor = null;
  return anchor;
}

export function connectColdStartRestore(
  store: GlobalStore,
  host: AppWorkerHost,
  openChat: (conversationKey: string) => void,
): () => void {
  const nav0 = store.get().nav;
  const top0 = topRoute(nav0);
  // 仅根路径冷启动才恢复（文件头口径）
  if (nav0.stack.length !== 1 || top0.kind !== 'home' || top0.tab !== 'chats') {
    return () => undefined;
  }

  let active = true;
  const offs: Array<() => void> = [];
  const cancel = (): void => {
    active = false;
    for (const off of offs.splice(0)) off();
  };

  // 期间任何导航（用户点击/守卫跳转）都放弃恢复
  offs.push(store.subscribe((s) => s.nav, cancel));

  let decided = false;
  const onAuth = (auth: AuthSlice): void => {
    if (!active || decided || auth.status === 'unknown') return;
    decided = true; // 首个决议定稿：logged-out/needsPassword 后再登录也不恢复
    if (auth.status !== 'logged-in' || auth.needsPassword) {
      cancel();
      return;
    }
    host.call('chat/restore/get', null).then(
      (point) => {
        if (!active || point === null) {
          cancel();
          return;
        }
        if (point.anchor !== undefined) {
          pendingAnchor = { key: point.chatKey, anchor: point.anchor };
        }
        cancel(); // 先解除 nav 订阅再导航（openChat 自己会改 nav）
        openChat(point.chatKey);
      },
      () => cancel(),
    );
  };
  offs.push(store.subscribe((s) => s.auth, onAuth));
  onAuth(store.get().auth);
  return cancel;
}
